import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import LayoutPicker from "../components/LayoutPicker";
import { siteVertical } from "../utils/vertical";

// The preview link is signed server-side. Open the window synchronously so
// the browser does not block it as an unrequested pop-up, then navigate it.
async function openPreviewWindow(previewUrl, templateId, layoutId) {
  const popup = window.open("", "_blank");
  const url = await previewUrl();
  if (!popup) return;
  if (!url) {
    popup.close();
    return;
  }
  let target = url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("template", templateId);
    parsed.searchParams.set("layout", layoutId);
    target = parsed.toString();
  } catch {
    // Use the URL as returned.
  }
  popup.opener = null;
  popup.location.href = target;
}

function DesignTab({ site, templates, saving, patch, previewUrl, hasBuilderPage }) {
  const [form, setForm] = useState({ template_id: site.template_id, layout_id: site.layout_id });

  useEffect(() => {
    setForm({ template_id: site.template_id, layout_id: site.layout_id });
  }, [site.id, site.template_id, site.layout_id]);

  const dirty = form.template_id !== site.template_id || form.layout_id !== site.layout_id;

  const openPreview = (templateId, layoutId) => openPreviewWindow(previewUrl, templateId, layoutId);

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
        <MDBox>
          <MDTypography variant="h6">Design</MDTypography>
          <MDTypography variant="body2" color="text">Choose the template and home page layout for this website.</MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="info" size="small" onClick={() => openPreview(form.template_id, form.layout_id)}>Preview current selection</MDButton>
      </MDBox>
      {hasBuilderPage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your home page was edited in the storefront builder, so it keeps its blocks when you switch layouts. To start again from
          the new layout, use &quot;Reset page to template layout&quot; in the builder.
        </Alert>
      )}
      <LayoutPicker
        templates={templates.filter((item) => (item.vertical || "travel") === siteVertical(site))}
        templateId={form.template_id}
        layoutId={form.layout_id}
        onChange={(change) => setForm(change)}
        onPreview={(layout, template) => openPreview(template.id, layout.id)}
        disabled={saving}
      />
      <MDBox display="flex" gap={1} mt={3} alignItems="center">
        <MDButton color="info" disabled={!dirty || saving || !form.layout_id} onClick={() => patch(form, "Design saved")}>
          {saving ? "Saving…" : "Save design"}
        </MDButton>
        {dirty && (
          <MDButton variant="text" color="dark" disabled={saving} onClick={() => setForm({ template_id: site.template_id, layout_id: site.layout_id })}>
            Discard changes
          </MDButton>
        )}
      </MDBox>
    </Card>
  );
}

DesignTab.propTypes = {
  site: PropTypes.object.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  saving: PropTypes.bool.isRequired,
  patch: PropTypes.func.isRequired,
  previewUrl: PropTypes.func.isRequired,
  hasBuilderPage: PropTypes.bool,
};

DesignTab.defaultProps = { hasBuilderPage: false };

export default DesignTab;
